import { useEffect, useState, useCallback } from "react";
import api, { apiError, API } from "@/lib/api";
import { Empty } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, fmtNOK } from "@/lib/labels";
import { Lock, Receipt, FileDown, Building2, Users2, BarChart3, Coins, TrendingDown, FileText } from "lucide-react";
import { toast } from "sonner";

const curMonth = () => new Date().toISOString().slice(0, 7);

function Stat({ label, value, accent }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <p className="label-caps">{label}</p>
      <p className={`font-display text-2xl mt-1 ${accent || ""}`}>{value}</p>
    </div>
  );
}

const PROFIT_STATUS = {
  over: ["Over budsjett", "bg-rose-500/15 text-rose-300 border-rose-500/30"],
  innenfor: ["Innenfor", "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"],
  ingen_timer: ["Ingen timer", "bg-secondary text-muted-foreground border-border"],
};

const AGREEMENT_TYPE_LABEL = { regnskap: "Regnskap", revisjon: "Revisjon", juridisk: "Juridisk", utleie: "Utleie" };

function agrStatus(a) {
  if (a.signed) return ["Signert", "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"];
  if (a.sign_status && a.sign_status !== "ikke_opprettet") return ["Til signering", "bg-amber-500/15 text-amber-300 border-amber-500/30"];
  if (a.internally_approved) return ["Godkjent internt", "bg-blue-500/15 text-blue-300 border-blue-500/30"];
  return ["Kladd", "bg-secondary text-muted-foreground border-border"];
}

export function AgreementsOverviewTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/agreements/overview").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  return (
    <div className="space-y-6">
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {data.by_type.map((t) => (
            <div key={t.type} className="bg-card border border-border rounded-xl p-5" data-testid={`agr-type-${t.type}`}>
              <p className="label-caps">{AGREEMENT_TYPE_LABEL[t.type]}</p>
              <p className="font-display text-2xl mt-1">{t.agreements}</p>
              <p className="text-xs text-muted-foreground mt-1">{t.customers} kunder · MRR {fmtNOK(t.mrr)}</p>
            </div>
          ))}
        </div>
      )}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-display text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-muted-foreground" /> Avtaler på tvers av kunder</h3>
          {data && <span className="text-sm text-muted-foreground">{data.totals.agreements} avtaler · {data.totals.customers} kunder · {data.totals.signed} signert</span>}
        </div>
        {!data || data.agreements.length === 0 ? (
          <Empty icon={FileText} title="Ingen avtaler ennå" hint="Generer avtaler på kundekortet under fanen «Avtaler»." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left">{["Kunde", "Avtale-ID", "Type", "Versjon", "Status", "MRR", "Opprettet", "Av"].map((h) => <th key={h} className="px-3 py-2 label-caps whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {data.agreements.map((a) => {
                  const [sl, scls] = agrStatus(a);
                  return (
                    <tr key={a.id} className="border-b border-border/50" data-testid={`agr-row-${a.id}`}>
                      <td className="px-3 py-2 font-medium whitespace-nowrap">{a.customer_name}</td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{a.agreement_id}</td>
                      <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded border border-border bg-secondary whitespace-nowrap">{AGREEMENT_TYPE_LABEL[a.agreement_type] || a.agreement_type}</span></td>
                      <td className="px-3 py-2">v{a.version}</td>
                      <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded border whitespace-nowrap ${scls}`}>{sl}</span></td>
                      <td className="px-3 py-2 whitespace-nowrap">{a.mrr ? fmtNOK(a.mrr) : "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{a.created_at ? fmtDate(a.created_at) : "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{a.created_by || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function InvoiceBasisTab({ month }) {
  const [overview, setOverview] = useState(null);
  const [runs, setRuns] = useState([]);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/invoice-basis?month=${month}`).then((r) => setOverview(r.data)).catch((e) => toast.error(apiError(e)));
    api.get(`/invoice-runs?month=${month}`).then((r) => setRuns(r.data)).catch(() => {});
  }, [month]);
  useEffect(() => { load(); setDetail(null); }, [load]);

  const openDetail = async (cid) => {
    try { const { data } = await api.get(`/invoice-basis/${cid}?month=${month}`); setDetail(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const lock = async (cid) => {
    if (!window.confirm("Marker som fakturert? Timene låses, og et PDF-fakturagrunnlag lagres under kundens dokumenter.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/invoice-basis/${cid}/lock`, { month });
      toast.success(`Fakturert: ${data.hours} t / ${fmtNOK(data.subtotal)} eks. mva. PDF lagret under kundens Dokumenter.`);
      setDetail(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg">Til fakturering · {month}</h3>
          {overview && <span className="text-sm text-muted-foreground">Sum eks. mva: <span className="text-foreground font-medium">{fmtNOK(overview.totals.amount)}</span></span>}
        </div>
        {!overview || overview.customers.length === 0 ? (
          <Empty icon={Receipt} title="Ingenting å fakturere" hint="Ingen fakturerbare, ikke-fakturerte timer denne måneden." />
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left">{["Kunde", "Org.nr", "Timer", "Beløp eks. mva", ""].map((h) => <th key={h} className="px-3 py-2 label-caps">{h}</th>)}</tr></thead>
            <tbody>
              {overview.customers.map((c) => (
                <tr key={c.customer_id} className="border-b border-border/50" data-testid={`invoice-cust-${c.customer_id}`}>
                  <td className="px-3 py-2 font-medium">{c.customer_name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{c.org_number || "—"}</td>
                  <td className="px-3 py-2">{c.hours}</td>
                  <td className="px-3 py-2">{fmtNOK(c.amount)}</td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <Button size="sm" variant="secondary" onClick={() => openDetail(c.customer_id)} data-testid={`invoice-view-${c.customer_id}`}>Vis grunnlag</Button>
                    <Button size="sm" onClick={() => lock(c.customer_id)} disabled={busy} className="bg-white text-black hover:bg-white/90" data-testid={`invoice-lock-${c.customer_id}`}><Lock className="w-4 h-4 mr-1" /> Fakturer</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detail && (
        <div className="bg-card border border-border rounded-xl p-6" data-testid="invoice-detail">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg">Fakturagrunnlag · {detail.customer_name}</h3>
            <button onClick={() => setDetail(null)} className="text-sm text-muted-foreground hover:text-foreground">Lukk</button>
          </div>
          <table className="w-full text-sm mb-4">
            <thead><tr className="border-b border-border text-left">{["Dato", "Aktivitet", "Beskrivelse", "Ansatt", "Timer", "Sats", "Beløp"].map((h) => <th key={h} className="px-3 py-2 label-caps">{h}</th>)}</tr></thead>
            <tbody>
              {detail.lines.map((ln) => (
                <tr key={ln.id} className="border-b border-border/50">
                  <td className="px-3 py-2 text-muted-foreground">{fmtDate(ln.date)}</td>
                  <td className="px-3 py-2">{ln.activity}</td>
                  <td className="px-3 py-2 text-muted-foreground">{ln.description || "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{ln.user_name}</td>
                  <td className="px-3 py-2">{ln.hours}</td>
                  <td className="px-3 py-2">{fmtNOK(ln.rate)}</td>
                  <td className="px-3 py-2 font-medium">{fmtNOK(ln.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex flex-col items-end gap-1 text-sm">
            <span>Sum eks. mva ({detail.totals.hours} t): <b>{fmtNOK(detail.totals.subtotal)}</b></span>
            <span className="text-muted-foreground">MVA 25%: {fmtNOK(detail.totals.vat)}</span>
            <span>Sum inkl. mva: <b>{fmtNOK(detail.totals.total_incl)}</b></span>
            <Button className="mt-3 bg-white text-black hover:bg-white/90" onClick={() => lock(detail.customer_id)} disabled={busy} data-testid="invoice-detail-lock"><Lock className="w-4 h-4 mr-1" /> Marker som fakturert & lag PDF</Button>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-display text-lg mb-4">Fakturert · {month}</h3>
        {runs.length === 0 ? <p className="text-sm text-muted-foreground">Ingen fakturagrunnlag er låst denne måneden ennå.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left">{["Kunde", "Timer", "Eks. mva", "Inkl. mva", "Fakturert av"].map((h) => <th key={h} className="px-3 py-2 label-caps">{h}</th>)}</tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-b border-border/50" data-testid={`invoice-run-${r.id}`}>
                  <td className="px-3 py-2 font-medium">{r.customer_name} <span className="ml-1 text-xs text-emerald-400">● fakturert</span></td>
                  <td className="px-3 py-2">{r.hours}</td>
                  <td className="px-3 py-2">{fmtNOK(r.subtotal)}</td>
                  <td className="px-3 py-2">{fmtNOK(r.total_incl)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.locked_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function ProfitabilityTab({ month }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/fixed-price-profitability?month=${month}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [month]);

  return (
    <div className="space-y-6">
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="Fastprisoppdrag" value={data.totals.count} />
          <Stat label="Over budsjett (reell)" value={data.totals.real_over_count} accent={data.totals.real_over_count > 0 ? "text-rose-300" : "text-emerald-400"} />
          <Stat label="Samlet margin (time)" value={fmtNOK(data.totals.margin)} accent={data.totals.margin < 0 ? "text-rose-300" : "text-emerald-400"} />
          <Stat label="Reell margin" value={fmtNOK(data.totals.real_margin)} accent={data.totals.real_margin < 0 ? "text-rose-300" : "text-emerald-400"} />
        </div>
      )}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="font-display text-lg flex items-center gap-2"><Coins className="w-5 h-5 text-muted-foreground" /> Fastpris-lønnsomhet · {month}</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4"><b>Margin (time)</b> = fastpris − (timer × timepris). <b>Reell margin</b> = fastpris − kostpris. Kostpris for <b>Utleie</b> = kundens månedlige kostpris; for øvrige = timer × (årslønn/1920 × 1,3) per ansatt. Negativ margin = oppdraget tærer på marginen.</p>
        {!data || data.customers.length === 0 ? (
          <Empty icon={TrendingDown} title="Ingen fastprisoppdrag med timer" hint="Ingen fastpris-kunder med produkter eller timer denne måneden." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left">{["Kunde", "Oppdragsansvarlig", "Fastpris/mnd", "Timer", "Verdi av timer", "Margin (time)", "Kostpris", "Reell margin", "Status"].map((h) => <th key={h} className="px-3 py-2 label-caps whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {data.customers.map((c) => {
                  const st = c.real_status || c.status;
                  const [sl, scls] = PROFIT_STATUS[st] || PROFIT_STATUS.ingen_timer;
                  return (
                    <tr key={c.customer_id} className="border-b border-border/50" data-testid={`profit-row-${c.customer_id}`}>
                      <td className="px-3 py-2 font-medium whitespace-nowrap">{c.customer_name}{c.is_utleie && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border border-border bg-secondary text-muted-foreground">Utleie</span>}</td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{c.engagement_manager_name || "—"}</td>
                      <td className="px-3 py-2">{fmtNOK(c.fixed_monthly)}</td>
                      <td className="px-3 py-2">{c.hours}</td>
                      <td className="px-3 py-2 text-muted-foreground">{fmtNOK(c.value)}</td>
                      <td className={`px-3 py-2 ${c.margin < 0 ? "text-rose-300" : "text-muted-foreground"}`}>{fmtNOK(c.margin)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{c.cost ? fmtNOK(c.cost) : "—"} {c.cost ? <span className="text-[10px] text-muted-foreground">({c.cost_basis === "kostpris" ? "utleie" : "lønn"})</span> : null}</td>
                      <td className={`px-3 py-2 font-medium whitespace-nowrap ${c.real_margin != null && c.real_margin < 0 ? "text-rose-300" : c.real_margin != null ? "text-emerald-400" : "text-muted-foreground"}`} data-testid={`profit-realmargin-${c.customer_id}`}>{c.real_margin != null ? `${fmtNOK(c.real_margin)} (${c.real_margin_pct}%)` : "—"}</td>
                      <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded border whitespace-nowrap ${scls}`}>{sl}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function TimeReportsTab() {
  const [range, setRange] = useState({ mode: "month", month: curMonth(), date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const paramsObj = useCallback(() => range.mode === "month"
    ? { month: range.month }
    : { date_from: range.date_from || undefined, date_to: range.date_to || undefined }, [range]);

  const load = useCallback(() => {
    const p = range.mode === "month" ? `month=${range.month}` : `date_from=${range.date_from}&date_to=${range.date_to}`;
    api.get(`/time-entries/summary?${p}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [range]);
  useEffect(() => { if (range.mode === "month" || (range.date_from && range.date_to)) load(); }, [load]);

  const exportPdf = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("murphy_token");
      const res = await fetch(`${API}/time-report/pdf`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify(paramsObj()) });
      if (!res.ok) throw new Error("Kunne ikke lage PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Timerapport ${range.mode === "month" ? range.month : range.date_from + "_" + range.date_to}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || "Noe gikk galt"); }
    finally { setBusy(false); }
  };

  const RTable = ({ title, items, nameKey, nameLabel, icon: Icon }) => (
    <div className="bg-card border border-border rounded-xl p-6">
      <h3 className="font-display text-lg mb-4 flex items-center gap-2">{Icon && <Icon className="w-5 h-5 text-muted-foreground" />} {title}</h3>
      {(!items || items.length === 0) ? <p className="text-sm text-muted-foreground">Ingen data.</p> : (
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left">{[nameLabel, "Timer", "Fakturerbart", "Beløp"].map((h) => <th key={h} className="px-3 py-2 label-caps">{h}</th>)}</tr></thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-b border-border/50">
                <td className="px-3 py-2 font-medium">{it[nameKey] || "—"}</td>
                <td className="px-3 py-2">{it.hours}</td>
                <td className="px-3 py-2 text-emerald-400">{it.billable_hours}</td>
                <td className="px-3 py-2">{fmtNOK(it.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-xl p-5 flex flex-wrap items-end gap-3">
        <div>
          <Label className="label-caps">Periode</Label>
          <Select value={range.mode} onValueChange={(v) => setRange({ ...range, mode: v })}>
            <SelectTrigger className="mt-1 w-40 bg-secondary border-border" data-testid="report-mode"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover border-border"><SelectItem value="month">Måned</SelectItem><SelectItem value="range">Datointervall</SelectItem></SelectContent>
          </Select>
        </div>
        {range.mode === "month" ? (
          <div><Label className="label-caps">Måned</Label><Input type="month" value={range.month} onChange={(e) => setRange({ ...range, month: e.target.value })} className="mt-1 w-40 bg-secondary border-border" data-testid="report-month" /></div>
        ) : (
          <>
            <div><Label className="label-caps">Fra</Label><Input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="report-from" /></div>
            <div><Label className="label-caps">Til</Label><Input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className="mt-1 bg-secondary border-border" data-testid="report-to" /></div>
          </>
        )}
        <Button className="ml-auto bg-white text-black hover:bg-white/90" onClick={exportPdf} disabled={busy} data-testid="report-export-pdf"><FileDown className="w-4 h-4 mr-1" /> {busy ? "Lager PDF…" : "Eksporter PDF"}</Button>
      </div>

      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="Timer totalt" value={data.total_hours} />
          <Stat label="Fakturerbart" value={data.billable_hours} accent="text-emerald-400" />
          <Stat label="Fakturerbar andel" value={`${data.billable_ratio}%`} />
          <Stat label="Fakturerbart beløp" value={fmtNOK(data.billable_amount)} accent="text-emerald-400" />
        </div>
      )}
      {data && <RTable title="Per kunde" items={data.by_customer} nameKey="customer_name" nameLabel="Kunde" icon={Building2} />}
      {data && <RTable title="Per ansatt" items={data.by_user} nameKey="user_name" nameLabel="Ansatt" icon={Users2} />}
      {data && <RTable title="Per aktivitet" items={data.by_activity} nameKey="activity" nameLabel="Aktivitet" icon={BarChart3} />}
    </div>
  );
}
